import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button, Field, Input, RadioGroup } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useEffect, useRef } from 'react';
import { Controller, type Control, type FieldError, useForm } from 'react-hook-form';

import { DAY_KEYS, storeFormSchema, type StoreFormValues } from '../schemas';
import { slugify, slugNeedsManualEntry } from '../slug';
import { SOCIAL_PLACEHOLDER, type SocialNetwork } from '../social-links';
import type { StoreConfiguration } from '../types';

import { BrandingImageField } from './BrandingImageField';
import { CollapsibleSection } from './CollapsibleSection';

import { whenDefined } from '@/lib/optional-props';

type SocialFieldName =
  | 'socialFacebook'
  | 'socialInstagram'
  | 'socialTiktok'
  | 'socialYoutube'
  | 'socialX'
  | 'socialLinkedin';

const SOCIAL_FIELDS: readonly {
  readonly name: SocialFieldName;
  readonly network: SocialNetwork;
  readonly labelKey: MessageKey;
}[] = [
  { name: 'socialFacebook', network: 'facebook', labelKey: 'merchant.store.form.socialFacebook' },
  {
    name: 'socialInstagram',
    network: 'instagram',
    labelKey: 'merchant.store.form.socialInstagram',
  },
  { name: 'socialTiktok', network: 'tiktok', labelKey: 'merchant.store.form.socialTiktok' },
  { name: 'socialYoutube', network: 'youtube', labelKey: 'merchant.store.form.socialYoutube' },
  { name: 'socialX', network: 'x', labelKey: 'merchant.store.form.socialX' },
  { name: 'socialLinkedin', network: 'linkedin', labelKey: 'merchant.store.form.socialLinkedin' },
];

type ColorFieldName =
  | 'colorPrimary'
  | 'colorSecondary'
  | 'colorAccent'
  | 'colorText'
  | 'colorButton'
  | 'colorBadge'
  | 'colorPromo'
  | 'colorVariant';

/**
 * Identité visuelle par marchand lot — a native `<input type="color">`
 * can never represent "no value" (the browser always reports some hex,
 * defaulting to black), so the field's OWN `null` ("inherit the platform
 * default") is represented here by a "Réinitialiser" text button next to
 * the swatch, never by the color input itself. `previewFallback` is
 * ONLY the swatch's own displayed color while the field is `null` — it is
 * never written to the form, never submitted, purely so an untouched
 * swatch doesn't show a jarring black square.
 */
function ColorField({
  name,
  label,
  hint,
  previewFallback,
  control,
  error,
  disabled,
  resetLabel,
}: {
  readonly name: ColorFieldName;
  readonly label: string;
  readonly hint: string;
  readonly previewFallback: string;
  readonly control: Control<StoreFormValues>;
  readonly error?: FieldError | undefined;
  readonly disabled?: boolean | undefined;
  readonly resetLabel: string;
}): ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Field label={label} optional hint={hint} {...whenDefined('error', error?.message)}>
          {(bind) => (
            <div className="flex items-center gap-2">
              <input
                {...bind}
                type="color"
                disabled={disabled === true}
                value={field.value ?? previewFallback}
                onChange={(event) => {
                  field.onChange(event.target.value);
                }}
                className="h-9 w-14 shrink-0 cursor-pointer rounded-md border border-line bg-transparent p-1"
              />
              <span className="font-mono text-xs text-inkFaint">
                {field.value ?? previewFallback}
              </span>
              {field.value !== null ? (
                <button
                  type="button"
                  disabled={disabled === true}
                  onClick={() => {
                    field.onChange(null);
                  }}
                  className="text-xs font-medium text-inkFaint underline-offset-2 hover:text-ink hover:underline"
                >
                  {resetLabel}
                </button>
              ) : null}
            </div>
          )}
        </Field>
      )}
    />
  );
}

function textOf(value: string | undefined): string {
  return value ?? '';
}

/**
 * Reorganised per the supervisor's UX remark (résumé — "perception de
 * complexité lors de la première configuration de la boutique"): Section 1
 * (name + slug — the only two fields `storeFormSchema` requires) is always
 * expanded; everything else is optional in the schema and now visually
 * optional too, folded by default. Nothing here changes what is validated,
 * what is submitted, or what the API accepts — `storeFormSchema` and the
 * single `onSubmit` below are untouched; only the JSX grouping around the
 * same `register()` calls changed.
 *
 * A section only starts folded when it is genuinely empty. Editing an
 * existing store whose coordonnées/policies/SEO are already filled in
 * shows them open immediately — folding data that is already there would
 * turn "less intimidating for a new merchant" into "hides my own content"
 * for every merchant who comes back to edit.
 */
function hasAnyValue(values: readonly (string | null | undefined)[]): boolean {
  return values.some((value) => value !== undefined && value !== null && value.trim() !== '');
}

const DAY_LABEL_KEY: Record<(typeof DAY_KEYS)[number], MessageKey> = {
  monday: 'storefront.footer.day.monday',
  tuesday: 'storefront.footer.day.tuesday',
  wednesday: 'storefront.footer.day.wednesday',
  thursday: 'storefront.footer.day.thursday',
  friday: 'storefront.footer.day.friday',
  saturday: 'storefront.footer.day.saturday',
  sunday: 'storefront.footer.day.sunday',
};

const EMPTY_OPENING_HOURS: StoreFormValues['openingHours'] = {
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
  sunday: '',
};

export function StoreForm({
  store,
  defaultValues,
  onSubmit,
  isSubmitting,
  submitLabel,
  disabled,
  suggestedName,
  takenSlug = null,
}: {
  /**
   * Upload réel du logo/bannière lot — the RAW store record, read
   * directly (never through `defaultValues`/`register()`): logo/banner
   * are no longer part of this form's own submitted payload at all, each
   * managed by its own upload mutation (`BrandingImageField`). `undefined`
   * for a merchant who has never configured a store yet.
   */
  store?: Pick<StoreConfiguration, 'logoUrl' | 'logoObjectKey' | 'bannerUrl' | 'bannerObjectKey'>;
  defaultValues?: Partial<StoreFormValues>;
  onSubmit: (values: StoreFormValues) => void;
  isSubmitting: boolean;
  submitLabel: string;
  /** True for a Staff account — `PUT /merchant/store` requires `merchant_owner` (store.controller.ts), never widened here. */
  disabled?: boolean;
  /**
   * CREATION only: the account's business name (`legalName`). When defined, the name and the slug start pre-filled from
   * it, and the slug FOLLOWS the name until the merchant edits the slug by hand. `undefined` for an existing shop — its
   * public address is never rewritten because its name changed.
   */
  suggestedName?: string;
  /** The slug the API just refused as already in use; the slug field shows the error until the value changes. */
  takenSlug?: string | null;
}): ReactElement {
  const { t } = useI18n();
  const followsAccount = suggestedName !== undefined;
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setFocus,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<StoreFormValues>({
    resolver: zodResolver(storeFormSchema),
    defaultValues: {
      ...(followsAccount && suggestedName !== ''
        ? { name: suggestedName, slug: slugify(suggestedName) }
        : {}),
      openingHours: EMPTY_OPENING_HOURS,
      stockDisplayPolicy: 'low_stock_only',
      deliveryFee: 0,
      colorPrimary: null,
      colorSecondary: null,
      colorAccent: null,
      colorText: null,
      colorButton: null,
      colorBadge: null,
      colorPromo: null,
      colorVariant: null,
      metaPixelId: null,
      socialFacebook: null,
      socialInstagram: null,
      socialTiktok: null,
      socialYoutube: null,
      socialX: null,
      socialLinkedin: null,
      ...defaultValues,
    },
  });

  // Has the merchant typed in the name / the slug themselves? Programmatic `setValue` never counts.
  const nameTouched = useRef(false);
  const slugTouched = useRef(false);

  // The account may arrive after the form first rendered (memberships load): fill in then, unless the merchant started.
  useEffect(() => {
    if (!followsAccount || suggestedName === '' || nameTouched.current) return;
    setValue('name', suggestedName);
    if (!slugTouched.current) setValue('slug', slugify(suggestedName));
  }, [followsAccount, suggestedName, setValue]);

  useEffect(() => {
    if (takenSlug !== null) setFocus('slug');
  }, [takenSlug, setFocus]);

  // Typed `string` by the form, but `undefined` until the first keystroke when nothing was pre-filled.
  const nameValue = textOf(watch('name'));
  const slugValue = textOf(watch('slug'));
  const slugIsTaken = takenSlug !== null && slugValue.trim().toLowerCase() === takenSlug;
  const needsManualSlug =
    followsAccount && slugValue.trim() === '' && slugNeedsManualEntry(nameValue);
  const slugHint = needsManualSlug
    ? t('merchant.store.form.slugManualHint')
    : t('merchant.store.form.slugHint');
  const slugError =
    errors.slug?.message ?? (slugIsTaken ? t('merchant.store.form.slugTaken') : undefined);

  const hasLogo = store?.logoObjectKey !== undefined && store.logoObjectKey !== null;
  const hasBanner = store?.bannerObjectKey !== undefined && store.bannerObjectKey !== null;
  const generalHasContent =
    hasAnyValue([defaultValues?.description, defaultValues?.tagline]) ||
    hasLogo ||
    hasAnyValue([store?.logoUrl ?? null]);
  const storyHasContent = hasAnyValue([defaultValues?.aboutTitle, defaultValues?.aboutBody]);
  const brandingHasContent =
    hasBanner ||
    hasAnyValue([store?.bannerUrl ?? null]) ||
    [
      defaultValues?.colorPrimary,
      defaultValues?.colorSecondary,
      defaultValues?.colorAccent,
      defaultValues?.colorText,
      defaultValues?.colorButton,
      defaultValues?.colorBadge,
      defaultValues?.colorPromo,
      defaultValues?.colorVariant,
    ].some((value) => value !== undefined && value !== null);
  const brandingAdvancedHasContent = [
    defaultValues?.colorText,
    defaultValues?.colorButton,
    defaultValues?.colorBadge,
    defaultValues?.colorPromo,
    defaultValues?.colorVariant,
  ].some((value) => value !== undefined && value !== null);
  const contactHasContent = hasAnyValue([
    defaultValues?.phone,
    defaultValues?.whatsapp,
    defaultValues?.email,
    defaultValues?.address,
    ...Object.values(defaultValues?.openingHours ?? {}),
  ]);
  const policyHasContent =
    hasAnyValue([
      defaultValues?.shippingPolicy,
      defaultValues?.returnPolicy,
      defaultValues?.termsOfService,
      defaultValues?.privacyPolicy,
    ]) || (defaultValues?.deliveryFee ?? 0) > 0;
  const seoHasContent = hasAnyValue([defaultValues?.seoTitle, defaultValues?.seoDescription]);
  const socialHasContent = hasAnyValue(SOCIAL_FIELDS.map(({ name }) => defaultValues?.[name]));
  const socialHasError = SOCIAL_FIELDS.some(({ name }) => errors[name] !== undefined);
  const pixelHasContent = hasAnyValue([defaultValues?.metaPixelId]);

  const optionalBadge = <span className="ecom-field__optional">{t('common.optional')}</span>;

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={disabled === true} className="flex flex-col gap-4">
        {/* Section 1 — Informations essentielles. The only two fields
            storeFormSchema actually requires; never folded. */}
        <section className="flex flex-col gap-4 rounded-md border border-line px-4 py-4">
          <h2 className="font-display text-base font-semibold text-ink">
            {t('merchant.store.form.essentials')}
          </h2>

          {followsAccount && suggestedName !== '' ? (
            <p className="rounded-md bg-infoSoft px-3 py-2 text-sm text-ink">
              {t('merchant.store.form.prefillNotice', { name: suggestedName })}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('merchant.store.form.nameLabel')}
              {...whenDefined('error', errors.name?.message)}
            >
              {(bind) => (
                <Input
                  {...bind}
                  {...register('name', {
                    onChange: (event: { target: { value: string } }) => {
                      nameTouched.current = true;
                      // The slug follows the name until the merchant has chosen one of their own.
                      if (followsAccount && !slugTouched.current) {
                        setValue('slug', slugify(event.target.value), {
                          shouldValidate: isSubmitted,
                        });
                      }
                    },
                  })}
                  autoFocus
                />
              )}
            </Field>

            <Field
              label={t('merchant.store.form.slugLabel')}
              hint={slugHint}
              {...whenDefined('error', slugError)}
            >
              {(bind) => (
                <Input
                  {...bind}
                  {...register('slug', {
                    onChange: (event: { target: { value: string } }) => {
                      // Typing a slug stops the following; emptying it hands the slug back to the name.
                      slugTouched.current = event.target.value.trim() !== '';
                    },
                  })}
                  dir="ltr"
                />
              )}
            </Field>
          </div>
        </section>

        {/* Section 2 — Informations générales */}
        <CollapsibleSection
          title={t('merchant.store.form.general')}
          defaultOpen={generalHasContent}
          forceOpen={errors.description !== undefined || errors.tagline !== undefined}
          badge={optionalBadge}
        >
          <BrandingImageField
            kind="logo"
            label={t('merchant.store.form.logoUrlLabel')}
            hint={t('merchant.store.form.logoUrlHint')}
            hasObjectKey={hasLogo}
            legacyUrl={store?.logoUrl ?? null}
            disabled={disabled}
          />

          <Field
            label={t('merchant.store.form.taglineLabel')}
            hint={t('merchant.store.form.taglineHint')}
            optional
            {...whenDefined('error', errors.tagline?.message)}
          >
            {(bind) => <Input {...bind} {...register('tagline')} maxLength={160} />}
          </Field>

          <Field
            label={t('merchant.store.form.descriptionLabel')}
            optional
            {...whenDefined('error', errors.description?.message)}
          >
            {(bind) => (
              <textarea {...bind} {...register('description')} rows={3} className="ecom-input" />
            )}
          </Field>
        </CollapsibleSection>

        {/* Section « Notre histoire » — le bloc de marque du storefront
            (qui êtes-vous, que vendez-vous, pourquoi existez-vous). Texte
            brut : les paragraphes se séparent par une ligne vide, aucun
            HTML ni Markdown n'est jamais interprété côté boutique. */}
        <CollapsibleSection
          title={t('merchant.store.form.story')}
          defaultOpen={storyHasContent}
          forceOpen={errors.aboutTitle !== undefined || errors.aboutBody !== undefined}
          badge={optionalBadge}
        >
          <Field
            label={t('merchant.store.form.storyTitleLabel')}
            hint={t('merchant.store.form.storyTitleHint')}
            optional
            {...whenDefined('error', errors.aboutTitle?.message)}
          >
            {(bind) => <Input {...bind} {...register('aboutTitle')} maxLength={120} />}
          </Field>
          <Field
            label={t('merchant.store.form.storyBodyLabel')}
            hint={t('merchant.store.form.storyBodyHint')}
            optional
            {...whenDefined('error', errors.aboutBody?.message)}
          >
            {(bind) => (
              <textarea
                {...bind}
                {...register('aboutBody')}
                rows={8}
                maxLength={2000}
                className="ecom-input"
              />
            )}
          </Field>
        </CollapsibleSection>

        {/* Section identité visuelle — "un moteur, mille identités" : le
            storefront de CETTE boutique adapte automatiquement Hero,
            boutons, badges et variantes à ces couleurs (résolution côté
            storefront, `resolveShopThemeStyle`). Aucune couleur n'est
            obligatoire — un champ laissé à "Réinitialiser" (valeur `null`)
            garde le rendu actuel de la plateforme, exactement comme avant
            ce lot. */}
        <CollapsibleSection
          title={t('merchant.store.form.branding')}
          defaultOpen={brandingHasContent}
          forceOpen={
            errors.colorPrimary !== undefined ||
            errors.colorSecondary !== undefined ||
            errors.colorAccent !== undefined
          }
          badge={optionalBadge}
        >
          <BrandingImageField
            kind="banner"
            label={t('merchant.store.form.bannerUrlLabel')}
            hint={t('merchant.store.form.bannerUrlHint')}
            hasObjectKey={hasBanner}
            legacyUrl={store?.bannerUrl ?? null}
            disabled={disabled}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <ColorField
              name="colorPrimary"
              label={t('merchant.store.form.colorPrimaryLabel')}
              hint={t('merchant.store.form.colorPrimaryHint')}
              previewFallback="#b5471e"
              control={control}
              error={errors.colorPrimary}
              disabled={disabled}
              resetLabel={t('merchant.store.form.colorReset')}
            />
            <ColorField
              name="colorSecondary"
              label={t('merchant.store.form.colorSecondaryLabel')}
              hint={t('merchant.store.form.colorSecondaryHint')}
              previewFallback="#1e6e76"
              control={control}
              error={errors.colorSecondary}
              disabled={disabled}
              resetLabel={t('merchant.store.form.colorReset')}
            />
            <ColorField
              name="colorAccent"
              label={t('merchant.store.form.colorAccentLabel')}
              hint={t('merchant.store.form.colorAccentHint')}
              previewFallback="#b5471e"
              control={control}
              error={errors.colorAccent}
              disabled={disabled}
              resetLabel={t('merchant.store.form.colorReset')}
            />
          </div>

          <CollapsibleSection
            title={t('merchant.store.form.brandingAdvanced')}
            defaultOpen={brandingAdvancedHasContent}
            forceOpen={
              errors.colorText !== undefined ||
              errors.colorButton !== undefined ||
              errors.colorBadge !== undefined ||
              errors.colorPromo !== undefined ||
              errors.colorVariant !== undefined
            }
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <ColorField
                name="colorText"
                label={t('merchant.store.form.colorTextLabel')}
                hint={t('merchant.store.form.colorTextHint')}
                previewFallback="#241c15"
                control={control}
                error={errors.colorText}
                disabled={disabled}
                resetLabel={t('merchant.store.form.colorReset')}
              />
              <ColorField
                name="colorButton"
                label={t('merchant.store.form.colorButtonLabel')}
                hint={t('merchant.store.form.colorButtonHint')}
                previewFallback="#b5471e"
                control={control}
                error={errors.colorButton}
                disabled={disabled}
                resetLabel={t('merchant.store.form.colorReset')}
              />
              <ColorField
                name="colorBadge"
                label={t('merchant.store.form.colorBadgeLabel')}
                hint={t('merchant.store.form.colorBadgeHint')}
                previewFallback="#1e6e76"
                control={control}
                error={errors.colorBadge}
                disabled={disabled}
                resetLabel={t('merchant.store.form.colorReset')}
              />
              <ColorField
                name="colorPromo"
                label={t('merchant.store.form.colorPromoLabel')}
                hint={t('merchant.store.form.colorPromoHint')}
                previewFallback="#b5471e"
                control={control}
                error={errors.colorPromo}
                disabled={disabled}
                resetLabel={t('merchant.store.form.colorReset')}
              />
              <ColorField
                name="colorVariant"
                label={t('merchant.store.form.colorVariantLabel')}
                hint={t('merchant.store.form.colorVariantHint')}
                previewFallback="#b5471e"
                control={control}
                error={errors.colorVariant}
                disabled={disabled}
                resetLabel={t('merchant.store.form.colorReset')}
              />
            </div>
          </CollapsibleSection>
        </CollapsibleSection>

        {/* Section 3 — Coordonnées (horaires d'ouverture inclus : ce sont,
            comme le téléphone ou l'adresse, des informations qui répondent
            à "comment/quand joindre ou visiter la boutique"). */}
        <CollapsibleSection
          title={t('storefront.footer.contact')}
          defaultOpen={contactHasContent}
          forceOpen={
            errors.phone !== undefined ||
            errors.whatsapp !== undefined ||
            errors.email !== undefined ||
            errors.address !== undefined
          }
          badge={optionalBadge}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('merchant.store.form.phoneLabel')}
              optional
              {...whenDefined('error', errors.phone?.message)}
            >
              {(bind) => <Input {...bind} {...register('phone')} />}
            </Field>
            <Field
              label={t('merchant.store.form.whatsappLabel')}
              hint={t('merchant.store.form.whatsappHint')}
              optional
              {...whenDefined('error', errors.whatsapp?.message)}
            >
              {(bind) => <Input {...bind} {...register('whatsapp')} type="tel" inputMode="tel" />}
            </Field>
            <Field
              label={t('merchant.store.form.emailLabel')}
              optional
              {...whenDefined('error', errors.email?.message)}
            >
              {(bind) => <Input {...bind} {...register('email')} type="email" />}
            </Field>
            <Field
              label={t('merchant.store.form.addressLabel')}
              optional
              {...whenDefined('error', errors.address?.message)}
            >
              {(bind) => <Input {...bind} {...register('address')} />}
            </Field>
          </div>

          <h3 className="text-sm font-medium text-ink">{t('storefront.footer.hours')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {DAY_KEYS.map((day) => (
              <Field key={day} label={t(DAY_LABEL_KEY[day])} optional>
                {(bind) => (
                  <Input {...bind} {...register(`openingHours.${day}`)} placeholder="09:00–18:00" />
                )}
              </Field>
            ))}
          </div>
        </CollapsibleSection>

        {/* Section 4 — Politique commerciale */}
        <CollapsibleSection
          title={t('storefront.footer.policies')}
          defaultOpen={policyHasContent}
          forceOpen={
            errors.shippingPolicy !== undefined ||
            errors.returnPolicy !== undefined ||
            errors.termsOfService !== undefined ||
            errors.privacyPolicy !== undefined ||
            errors.deliveryFee !== undefined
          }
          badge={optionalBadge}
        >
          <Field
            label={t('storefront.footer.shipping')}
            optional
            {...whenDefined('error', errors.shippingPolicy?.message)}
          >
            {(bind) => (
              <textarea {...bind} {...register('shippingPolicy')} rows={3} className="ecom-input" />
            )}
          </Field>
          <Field
            label={t('storefront.footer.returns')}
            optional
            {...whenDefined('error', errors.returnPolicy?.message)}
          >
            {(bind) => (
              <textarea {...bind} {...register('returnPolicy')} rows={3} className="ecom-input" />
            )}
          </Field>
          <Field
            label={t('storefront.footer.terms')}
            optional
            {...whenDefined('error', errors.termsOfService?.message)}
          >
            {(bind) => (
              <textarea {...bind} {...register('termsOfService')} rows={3} className="ecom-input" />
            )}
          </Field>
          <Field
            label={t('storefront.footer.privacy')}
            optional
            {...whenDefined('error', errors.privacyPolicy?.message)}
          >
            {(bind) => (
              <textarea {...bind} {...register('privacyPolicy')} rows={3} className="ecom-input" />
            )}
          </Field>

          <Field
            label={t('merchant.store.form.deliveryFeeLabel')}
            optional
            hint={t('merchant.store.form.deliveryFeeHint')}
            {...whenDefined('error', errors.deliveryFee?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('deliveryFee', { valueAsNumber: true })}
                type="number"
                step="0.001"
                min="0"
              />
            )}
          </Field>
        </CollapsibleSection>

        {/* Réseaux sociaux (migration 0096) — shown as icons at the top of the storefront, only when filled in. */}
        <CollapsibleSection
          title={t('merchant.store.form.social')}
          defaultOpen={socialHasContent}
          forceOpen={socialHasError}
          badge={optionalBadge}
        >
          <p className="text-sm text-inkSoft">{t('merchant.store.form.socialHint')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {SOCIAL_FIELDS.map(({ name, network, labelKey }) => (
              <Field
                key={name}
                label={t(labelKey)}
                optional
                {...whenDefined('error', errors[name]?.message)}
              >
                {(bind) => (
                  <Input
                    {...bind}
                    {...register(name)}
                    type="url"
                    inputMode="url"
                    dir="ltr"
                    placeholder={SOCIAL_PLACEHOLDER[network]}
                  />
                )}
              </Field>
            ))}
          </div>
          <p className="text-xs text-inkSoft">{t('merchant.store.form.socialWhatsappNote')}</p>
        </CollapsibleSection>

        {/* Meta Pixel (migration 0096) — Facebook & Instagram ads tracking on the public shop pages. */}
        <CollapsibleSection
          title={t('merchant.store.form.metaPixel')}
          defaultOpen={pixelHasContent}
          forceOpen={errors.metaPixelId !== undefined}
          badge={optionalBadge}
        >
          <Field
            label={t('merchant.store.form.metaPixelLabel')}
            hint={t('merchant.store.form.metaPixelHint')}
            optional
            {...whenDefined('error', errors.metaPixelId?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('metaPixelId')}
                inputMode="numeric"
                dir="ltr"
                autoComplete="off"
                placeholder="123456789012345"
              />
            )}
          </Field>
          <p className="text-xs text-inkSoft">{t('merchant.store.form.metaPixelPrivacy')}</p>
        </CollapsibleSection>

        {/* Section 5 — SEO */}
        <CollapsibleSection
          title={t('merchant.store.form.seo')}
          defaultOpen={seoHasContent}
          forceOpen={errors.seoTitle !== undefined || errors.seoDescription !== undefined}
          badge={optionalBadge}
        >
          <Field
            label={t('merchant.store.form.seoTitleLabel')}
            optional
            {...whenDefined('error', errors.seoTitle?.message)}
          >
            {(bind) => <Input {...bind} {...register('seoTitle')} />}
          </Field>
          <Field
            label={t('merchant.store.form.seoDescriptionLabel')}
            optional
            {...whenDefined('error', errors.seoDescription?.message)}
          >
            {(bind) => <Input {...bind} {...register('seoDescription')} />}
          </Field>
        </CollapsibleSection>

        {/* Section 6 — Politique d'affichage du stock (Jalon C, item 7).
            Always open, unlike the optional sections above: it is a real
            store-wide setting with a meaningful default, not empty content
            a new merchant would find intimidating. */}
        <section className="flex flex-col gap-4 rounded-md border border-line px-4 py-4">
          <h2 className="font-display text-base font-semibold text-ink">
            {t('merchant.store.form.stockDisplayPolicy')}
          </h2>
          <Controller
            control={control}
            name="stockDisplayPolicy"
            render={({ field }) => (
              <RadioGroup
                legend={t('merchant.store.form.stockDisplayPolicy')}
                name={field.name}
                value={field.value}
                disabled={disabled === true}
                onChange={field.onChange}
                options={[
                  {
                    value: 'exact',
                    label: t('merchant.store.form.stockDisplayPolicy.exact'),
                    description: t('merchant.store.form.stockDisplayPolicy.exact.hint'),
                  },
                  {
                    value: 'low_stock_only',
                    label: t('merchant.store.form.stockDisplayPolicy.lowStockOnly'),
                    description: t('merchant.store.form.stockDisplayPolicy.lowStockOnly.hint'),
                  },
                  {
                    value: 'hidden',
                    label: t('merchant.store.form.stockDisplayPolicy.hidden'),
                    description: t('merchant.store.form.stockDisplayPolicy.hidden.hint'),
                  },
                ]}
              />
            )}
          />
        </section>

        <div>
          <Button type="submit" loading={isSubmitting}>
            {submitLabel}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
